// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PolicyManifest } from '@/authorization/policy-manifest';
import { Permission, type PermissionAction } from '@/authorization/types';
import { useMemo } from 'react';

/**
 * Hook that provides access to the current user's policy manifest.
 * Returns null if the user has no policy or the policy is null.
 */
function useUserPolicy() {
  const { user } = useCurrentUser();

  const policyManifest = useMemo(() => {
    if (!user.activePolicy) {
      return null;
    }

    try {
      return new PolicyManifest(user.activePolicy);
    } catch (error) {
      console.error('Failed to parse user policy:', error);
      return null;
    }
  }, [user.activePolicy]);

  return policyManifest;
}

/**
 * Hook that provides a function to check permissions for the current user.
 * Returns a function that checks if the user has permission for a resource and action.
 * If no policy is available, all permissions return PermissionNotFound.
 */
export function usePermissionChecker() {
  const policyManifest = useUserPolicy();

  const hasPermission = useMemo(() => {
    if (!policyManifest) {
      // Return a function that always returns PermissionNotFound
      return () => Permission.PermissionNotFound;
    }

    return (resource: string, action: PermissionAction) => {
      return policyManifest.hasPermission(resource, action);
    };
  }, [policyManifest]);

  return {
    hasPermission,
    policyManifest,
  };
}
